<template>
  <div id="shopify-config-page">
    <vx-card title="Shopify — Connection" class="mb-base">
      <form autocomplete="off" @submit.prevent="save">
        <input type="text" name="fakeuser" autocomplete="username" style="display:none" />
        <input type="password" name="fakepass" autocomplete="current-password" style="display:none" />

        <div class="vx-row">
          <div class="vx-col md:w-1/2 w-full">
            <p class="mb-4">
              Configure the connection to your Shopify store. The app will use your Client ID / Secret
              to automatically obtain a short-lived Admin API token (refreshed every 24 h).
            </p>

            <vs-input
              label="Store domain (e.g. wew5cy-az.myshopify.com)"
              v-model="shop_domain"
              class="w-full mt-4"
              placeholder="wew5cy-az.myshopify.com"
              autocomplete="off"
              name="shopify_shop_domain"
            />

            <vs-input
              label="Client ID"
              v-model="client_id"
              class="w-full mt-4"
              :placeholder="clientIdPlaceholder"
              autocomplete="off"
              name="shopify_client_id"
            />

            <vs-input
              label="Client Secret (shpss_...)"
              v-model="client_secret"
              type="password"
              class="w-full mt-4"
              :placeholder="clientSecretPlaceholder"
              autocomplete="new-password"
              name="shopify_client_secret"
            />

            <div class="mt-4 text-center text-sm text-grey"><b>OR</b> paste a static Admin API token (custom app)</div>

            <vs-input
              label="Static access token (shpat_...)"
              v-model="access_token"
              type="password"
              class="w-full mt-2"
              :placeholder="accessTokenPlaceholder"
              autocomplete="new-password"
              name="shopify_access_token"
            />

            <vs-input
              label="API version"
              v-model="api_version"
              class="w-full mt-4"
              placeholder="2025-01"
              autocomplete="off"
              name="shopify_api_version"
            />

            <div class="mt-6 flex flex-wrap">
              <vs-button color="primary" class="mr-3 mb-2" @click.prevent="save">Save</vs-button>
              <vs-button color="success" class="mr-3 mb-2" :disabled="!config || !config.shop_domain" @click.prevent="test">
                Test connection
              </vs-button>
            </div>

            <div v-if="config" class="mt-6 text-sm">
              <p><b>Last connection:</b>
                <span v-if="config.last_test_at">
                  {{ formatDate(config.last_test_at) }}
                  <vs-chip v-if="config.last_test_ok" color="success">OK</vs-chip>
                  <vs-chip v-else color="danger">ERROR</vs-chip>
                </span>
                <span v-else>never</span>
              </p>
              <p v-if="config.last_test_message"><b>Message:</b> {{ config.last_test_message }}</p>
              <p v-if="config.location_name"><b>Active location:</b> {{ config.location_name }}</p>
              <p v-if="config.access_token_expires_at">
                <b>Token expires:</b> {{ formatDate(config.access_token_expires_at) }}
              </p>
            </div>
          </div>

          <div class="vx-col md:w-1/2 w-full">
            <vx-card class="p-4 bg-grey-light">
              <h4 class="mb-2">How to obtain Client ID / Secret</h4>
              <ol class="ml-4">
                <li>Open the <a href="https://partners.shopify.com/" target="_blank">Dev Dashboard</a> → your app.</li>
                <li>Open <b>Settings</b> → copy <code>Client ID</code> and <code>Client Secret</code>.</li>
                <li>Make sure Admin API scopes include: <code>read_products, write_products, read_inventory, write_inventory, read_locations, read_files, write_files</code>.</li>
                <li>Paste the two values above and click <b>Save</b>, then <b>Test connection</b>.</li>
              </ol>
              <p class="mt-2 text-sm">
                The server will exchange these credentials against Shopify's OAuth endpoint
                to receive a fresh <code>shpat_...</code> token valid 24 h and auto-refresh it on demand.
              </p>
            </vx-card>

            <vs-alert class="mt-4" color="warning" active="true" v-if="lastTest">
              <div v-if="lastTest.shop">
                <p><b>Shop:</b> {{ lastTest.shop.name }} ({{ lastTest.shop.currencyCode }})</p>
                <p v-if="lastTest.selectedLocation"><b>Detected location:</b> {{ lastTest.selectedLocation.name }}</p>
                <div v-if="lastTest.locations && lastTest.locations.length">
                  <b>All locations:</b>
                  <ul class="ml-4">
                    <li v-for="l in lastTest.locations" :key="l.id">{{ l.name }}</li>
                  </ul>
                </div>
              </div>
            </vs-alert>
          </div>
        </div>
      </form>
    </vx-card>
  </div>
</template>

<script>
import moduleShopify from '@/store/shopify/moduleShopify.js'

export default {
  data() {
    return {
      shop_domain: '',
      client_id: '',
      client_secret: '',
      access_token: '',
      api_version: '',
    }
  },
  computed: {
    config() {
      return this.$store.state.shopify ? this.$store.state.shopify.config : null
    },
    lastTest() {
      return this.$store.state.shopify ? this.$store.state.shopify.lastTest : null
    },
    clientIdPlaceholder() {
      return this.config && this.config.client_id ? this.config.client_id : ''
    },
    clientSecretPlaceholder() {
      return this.config && this.config.client_secret ? this.config.client_secret : 'shpss_...'
    },
    accessTokenPlaceholder() {
      return this.config && this.config.access_token ? this.config.access_token : 'shpat_...'
    },
  },
  methods: {
    formatDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleString('en-GB')
    },
    save() {
      const payload = {}
      if (this.shop_domain && this.shop_domain.trim() !== '') payload.shop_domain = this.shop_domain.trim()
      if (this.client_id && this.client_id.trim() !== '') payload.client_id = this.client_id.trim()
      if (this.client_secret && this.client_secret.trim() !== '') payload.client_secret = this.client_secret
      if (this.access_token && this.access_token.trim() !== '') payload.access_token = this.access_token.trim()
      if (this.api_version && this.api_version.trim() !== '') payload.api_version = this.api_version.trim()

      this.$store.dispatch('shopify/saveConfig', payload)
        .then(() => {
          this.shop_domain = ''
          this.client_id = ''
          this.client_secret = ''
          this.access_token = ''
          this.api_version = ''
          this.$vs.notify({
            color: 'success',
            title: 'Configuration saved',
            text: 'The Shopify connection has been saved.',
          })
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Error',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
          })
        })
    },
    test() {
      this.$store.dispatch('shopify/testConnection')
        .then(res => {
          this.$vs.notify({
            color: 'success',
            title: 'Connection OK',
            text: `Shop ${res.shop.name} — location: ${res.selectedLocation ? res.selectedLocation.name : '—'}`,
            time: 8000,
          })
          this.$store.dispatch('shopify/fetchConfig')
        })
        .catch(err => {
          this.$vs.notify({
            color: 'danger',
            title: 'Connection error',
            text: (err.response && err.response.data && err.response.data.message) || err.message,
            time: 10000,
          })
        })
    },
  },
  created() {
    if (!this.$store.state.shopify) {
      this.$store.registerModule('shopify', moduleShopify)
      moduleShopify.isRegistered = true
    }
    this.$store.dispatch('shopify/fetchConfig')
  },
}
</script>
